import { useEffect, useState, useCallback } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtMYR, fmtL, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, FileText, Pencil, Trash2, Loader2, Wallet, AlertTriangle, ArrowUpRight } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";

export default function CreditCustomers() {
  const { user } = useAuth();
  const { stationId, stations } = useStation();
  const isManager = user?.role === "manager";
  const canManage = ["manager", "station_admin", "staff"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact: "", opening_balance: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // customer for txn dialog
  const [txns, setTxns] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    const q = stationId !== "all" ? `?station_id=${stationId}` : "";
    api.get(`/credit/reconciliation${q}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [stationId]);
  useEffect(() => { load(); }, [load]);

  const submitAdd = async () => {
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/credit/customers/${editing.id}`, { company_name: form.company_name, contact: form.contact });
        toast.success("Customer updated");
      } else {
        await api.post("/credit/customers", { company_name: form.company_name, contact: form.contact, opening_balance: Number(form.opening_balance || 0), station_id: stationId !== "all" ? stationId : (stations[0]?.id || "") });
        toast.success("Customer added");
      }
      setAddOpen(false); setEditing(null); setForm({ company_name: "", contact: "", opening_balance: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };

  const openEdit = (c) => { setEditing(c); setForm({ company_name: c.company_name, contact: c.contact || "", opening_balance: "" }); setAddOpen(true); };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.company_name} and all their credit history? This cannot be undone.`)) return;
    try { await api.delete(`/credit/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const openDetail = async (c) => {
    setDetail(c); setTxns([]);
    try { const { data: t } = await api.get(`/credit/transactions?customer_id=${c.id}`); setTxns(t); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const statement = async (c) => {
    try {
      const res = await api.get(`/credit/statement/${c.id}?month=${month}`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = u; a.download = `statement-${c.company_name}-${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Statement downloaded");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const customers = data?.customers || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Receivables</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <Users className="w-8 h-8 text-emerald-400" /> Credit Customers
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Company accounts, pre-payments and outstanding fuel credit — reconciled across all stations.</p>
        </div>
        <div className="flex gap-3 items-end">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Statement month</Label>
            <Input type="month" data-testid="credit-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
          </div>
          {canManage && (
            <Button onClick={() => { setEditing(null); setForm({ company_name: "", contact: "", opening_balance: "" }); setAddOpen(true); }} data-testid="credit-add-customer" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Add Customer
            </Button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={`${card} p-5`} data-testid="credit-stat-outstanding">
              <div className="flex items-center gap-2 text-red-400 text-xs uppercase tracking-wider"><AlertTriangle className="w-4 h-4" /> Total Outstanding</div>
              <div className="metric-value text-2xl font-bold text-white mt-2">{fmtMYR(data.total_outstanding)}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">Owed to you across all customers</div>
            </div>
            <div className={`${card} p-5`} data-testid="credit-stat-available">
              <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-wider"><Wallet className="w-4 h-4" /> Pre-paid Credit</div>
              <div className="metric-value text-2xl font-bold text-white mt-2">{fmtMYR(data.total_credit_available)}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">Advance balances held for customers</div>
            </div>
            <div className={`${card} p-5`} data-testid="credit-stat-count">
              <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><Users className="w-4 h-4" /> Customers</div>
              <div className="metric-value text-2xl font-bold text-white mt-2">{data.count}</div>
              <div className="text-xs text-[#9CA3AF] mt-1">Registered credit accounts</div>
            </div>
          </div>

          <div className={`${card} p-5 sm:p-6`}>
            {customers.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="credit-customers-table">
                  <thead>
                    <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                      {["Company", "Contact", "Last Activity", "Total Fueled", "Pre-paid", "Balance", ""].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]" data-testid={`credit-row-${c.id}`}>
                        <td className="py-2.5 pr-4">
                          <button onClick={() => openDetail(c)} data-testid={`credit-view-${c.id}`} className="text-white font-medium hover:text-emerald-400 inline-flex items-center gap-1">
                            {c.company_name} <ArrowUpRight className="w-3.5 h-3.5 opacity-60" />
                          </button>
                        </td>
                        <td className="py-2.5 pr-4 text-[#9CA3AF]">{c.contact || "—"}</td>
                        <td className="py-2.5 pr-4 metric-value text-white/80" data-testid={`credit-lastdate-${c.id}`}>{c.last_date ? fmtDate(c.last_date) : "—"}</td>
                        <td className="py-2.5 pr-4 metric-value">{fmtMYR(c.total_sales)}</td>
                        <td className="py-2.5 pr-4 metric-value text-emerald-400/90">{fmtMYR(c.total_prepaid)}</td>
                        <td className={`py-2.5 pr-4 metric-value font-semibold ${c.balance < 0 ? "text-red-400" : "text-emerald-400"}`} data-testid={`credit-balance-${c.id}`}>
                          {fmtMYR(c.balance)}{c.balance < 0 ? " owed" : c.balance > 0 ? " credit" : ""}
                        </td>
                        <td className="py-2.5 pr-2 text-right whitespace-nowrap">
                          <button onClick={() => statement(c)} data-testid={`credit-statement-${c.id}`} title="Download monthly statement (PDF)" className="text-blue-400 hover:text-blue-300 mr-3"><FileText className="w-4 h-4 inline" /></button>
                          {canManage && <button onClick={() => openEdit(c)} data-testid={`credit-edit-${c.id}`} className="text-emerald-400 hover:text-emerald-300 mr-3"><Pencil className="w-4 h-4 inline" /></button>}
                          {isManager && <button onClick={() => del(c)} data-testid={`credit-delete-${c.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4 inline" /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-[#9CA3AF] py-12 text-center" data-testid="credit-empty">No credit customers yet. {canManage ? "Add one to start tracking fuel on account." : ""}</div>}
            <p className="text-xs text-[#9CA3AF] mt-3">Record credit fuel sales & pre-payments from the <b className="text-white">Credit</b> tab in Daily Operations. A negative balance means the customer owes you.</p>
          </div>
        </>
      )}

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); setEditing(null); } }}>
        <DialogContent className="bg-[#0B0F17] border-[#1F2937] text-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "Add Credit Customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#9CA3AF] text-xs">Company name</Label><Input data-testid="credit-form-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className={inp} /></div>
            <div><Label className="text-[#9CA3AF] text-xs">Contact (phone / person)</Label><Input data-testid="credit-form-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={inp} /></div>
            {!editing && (
              <div><Label className="text-[#9CA3AF] text-xs">Opening balance (RM) — positive = pre-paid credit</Label><Input type="number" step="0.01" data-testid="credit-form-opening" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className={inp} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setEditing(null); }} className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">Cancel</Button>
            <Button onClick={submitAdd} disabled={saving} data-testid="credit-form-save" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0B0F17] border-[#1F2937] text-white max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.company_name} · Ledger</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {txns.length ? (
              <table className="w-full text-sm" data-testid="credit-detail-table">
                <thead><tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono border-b border-[#1F2937]">
                  {["Date", "Type", "Product", "Litres", "Amount"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.id} className="border-b border-[#1F2937]/60">
                      <td className="py-2 pr-3 metric-value text-white/80">{fmtDate(t.date)}</td>
                      <td className="py-2 pr-3">{t.type === "prepayment" ? <span className="text-emerald-400">Pre-payment</span> : <span className="text-amber-400">Fuel sale</span>}</td>
                      <td className="py-2 pr-3">{t.product || "—"}</td>
                      <td className="py-2 pr-3 metric-value">{t.type === "sale" ? fmtL(t.liters || 0) : "—"}</td>
                      <td className={`py-2 pr-3 metric-value ${t.type === "prepayment" ? "text-emerald-400" : "text-amber-400"}`}>{t.type === "prepayment" ? "+" : "−"}{fmtMYR(t.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-sm text-[#9CA3AF] py-8 text-center">No transactions yet for this customer.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
